// akunkas.view — tab 💵 Akun Kas: daftar tempat uang toko berada (laci tunai, tiap rekening
// bank, penampung QRIS). Dari sinilah pilihan "cara bayar" kasir & baris Tutup Kasir berasal.
import { rp, rpFmt, rpVal, esc } from '@domain/format';
import { todayISO } from '@domain/format';
import { KELOMPOK_ASET } from '@domain/finansial';
import * as repo from '@modules/payments/akunkas.repo';
import { listCabang } from '@modules/cabang/cabang.repo';
import { openModal } from '@ui/modal';
import { toast } from '@ui/toast';

const JENIS: [repo.AkunRow['jenis'], string][] = [
  ['tunai', '💵 Tunai (laci kasir)'],
  ['bank', '🏦 Rekening bank'],
  ['qris', '📱 QRIS (penampung, cair H+1)'],
  ['ewallet', '👛 E-wallet'],
];
const namaJenis = (j: string): string => JENIS.find(([k]) => k === j)?.[1] ?? j;

export async function akunKasTab(body: HTMLElement, canEdit: boolean): Promise<void> {
  body.innerHTML = '<div class="panel">Memuat akun kas…</div>';
  let akun: repo.AkunRow[];
  try { akun = await repo.listAkun(true); } catch (e) { body.innerHTML = `<div class="panel"><div class="empty">${esc((e as Error).message)}</div></div>`; return; }

  const baris = akun.map((a) => `<tr${a.aktif ? '' : ' style="opacity:.55"'}>
    <td><b>${esc(a.nama)}</b>${a.kodeAkun ? ` <span class="pill" style="background:var(--ice);color:var(--navy);font-size:10px">${esc(a.kodeAkun)}</span>` : ''}
      ${a.aktif ? '' : ' <span class="pill" style="background:#eee;color:#666">nonaktif</span>'}
      ${a.catatan ? `<div style="font-size:11px;color:var(--muted)">${esc(a.catatan)}</div>` : ''}</td>
    <td>${esc(namaJenis(a.jenis))}</td>
    <td>${esc(a.outlet ?? 'Semua cabang')}</td>
    <td>${a.bankNama ? `${esc(a.bankNama)}${a.noRek ? ` · ${esc(a.noRek)}` : ''}${a.atasNama ? `<br><span style="font-size:11px;color:var(--muted)">a/n ${esc(a.atasNama)}</span>` : ''}` : '—'}</td>
    <td class="r">${a.jenis === 'tunai' ? `<b>${rp(a.kasKecil)}</b>` : '—'}</td>
    <td class="r">${rp(a.saldoAwal)}${a.tglAwal ? `<div style="font-size:11px;color:var(--muted)">per ${esc(a.tglAwal)}</div>` : ''}</td>
    <td style="white-space:nowrap">${canEdit ? `<button class="btn" style="padding:3px 8px" data-edit="${a.id}">✏️</button>
      <button class="btn" style="padding:3px 8px" data-aktif="${a.id}">${a.aktif ? '🚫 Nonaktifkan' : '↩︎ Aktifkan'}</button>` : '—'}</td>
  </tr>`).join('') || '<tr><td colspan="7" class="empty">Belum ada akun kas.</td></tr>';

  body.innerHTML = `<div class="panel">
    <div class="scope-note">Tiap tempat uang berada dicatat terpisah supaya saldonya bisa <b>dicocokkan ke bukti</b>:
      laci dihitung fisik, rekening dicocokkan mutasi bank, QRIS dicocokkan laporan settlement.
      Nama akun inilah yang muncul sebagai pilihan <b>cara bayar</b> di kasir — jadi tulis seperti yang diucapkan
      petugas, mis. <i>Transfer BCA</i>. <b>Kode akun (COA)</b> menautkan akun ini ke Neraca — mis. <i>1-1-2</i> Bank BCA muncul di bawah <i>1-1 Kas &amp; Bank</i>. <b>Saldo awal</b> tiap akun jadi saldo pembuka di Neraca.</div>
    ${canEdit ? '<button class="btn primary" id="ak_baru" style="margin-bottom:10px">➕ Akun kas baru</button>' : ''}
    <div style="overflow-x:auto"><table class="data">
      <thead><tr><th>Akun</th><th>Jenis</th><th>Cabang</th><th>Bank / No. rekening</th><th class="r">Kas kecil</th><th class="r">Saldo awal</th><th>Aksi</th></tr></thead>
      <tbody>${baris}</tbody></table></div>
  </div>`;

  body.querySelector('#ak_baru')?.addEventListener('click', () => void form(null, body));
  body.querySelectorAll<HTMLElement>('button[data-edit]').forEach((b) => b.addEventListener('click', () =>
    void form(akun.find((a) => a.id === +(b.dataset['edit'] ?? 0)) ?? null, body)));
  body.querySelectorAll<HTMLElement>('button[data-aktif]').forEach((b) => b.addEventListener('click', async () => {
    const a = akun.find((x) => x.id === +(b.dataset['aktif'] ?? 0)); if (!a) return;
    // Sengaja tak ada tombol hapus: akun yang pernah dipakai harus tetap ada agar riwayat
    // pembayaran & mutasi lamanya tak jadi yatim.
    try { await repo.nonaktifkanAkun(a.id, !a.aktif); toast('✅ Disimpan'); void akunKasTab(body, canEdit); }
    catch (e) { toast((e as Error).message); }
  }));
}

async function form(a: repo.AkunRow | null, body: HTMLElement): Promise<void> {
  const outlets = await listCabang().catch(() => []);
  const bg = openModal(`
    <div class="modal-head"><h3>${a ? '✏️ Ubah' : '➕'} Akun Kas</h3><button class="x">×</button></div>
    <div class="modal-body">
      <div class="row2">
        <div class="field"><label>Nama akun (tampil di pilihan cara bayar)</label>
          <input id="ak_nama" value="${esc(a?.nama ?? '')}" placeholder="Transfer BCA"></div>
        <div class="field"><label>Jenis</label><select id="ak_jenis">${JENIS.map(([k, l]) =>
          `<option value="${k}" ${a?.jenis === k ? 'selected' : ''}>${esc(l)}</option>`).join('')}</select></div>
      </div>
      <div class="row2">
        <div class="field"><label>Cabang</label><select id="ak_outlet"><option value="">Semua cabang</option>
          ${outlets.map((o) => `<option value="${esc(o.kode)}" ${a?.outlet === o.kode ? 'selected' : ''}>${esc(o.nama)}</option>`).join('')}</select></div>
        <div class="field"><label>Urutan tampil</label><input id="ak_urut" type="number" value="${a?.urut ?? 10}"></div>
      </div>
      <div id="ak_bankbox">
        <div class="row2">
          <div class="field"><label>Nama bank</label><input id="ak_bank" value="${esc(a?.bankNama ?? '')}" placeholder="BCA"></div>
          <div class="field"><label>No. rekening</label><input id="ak_rek" value="${esc(a?.noRek ?? '')}"></div>
        </div>
        <div class="field"><label>Atas nama</label><input id="ak_an" value="${esc(a?.atasNama ?? '')}"></div>
      </div>
      <div class="field" id="ak_kkbox"><label>Kas kecil — saldo laci tetap</label>
        <input id="ak_kk" inputmode="numeric" value="${rp(a?.kasKecil ?? 0)}">
        <div class="hint">Uang yang selalu ditinggal di laci tiap pagi (model imprest). Jadi <b>saldo awal otomatis</b>
          di Tutup Kasir — kasir tak perlu mengarang angka, tugasnya menghitung fisik. Sore hari sisanya disetor
          sampai laci kembali ke angka ini. Kalau uangnya kurang, kasir menanyakannya ke Finance.</div></div>
      <div class="row2">
        <div class="field"><label>Saldo awal</label><input id="ak_saldo" inputmode="numeric" value="${rp(a?.saldoAwal ?? 0)}">
          <div class="hint">Uang yang sudah ada saat mulai memakai aplikasi. Jadi titik mulai saldo berjalan.</div></div>
        <div class="field"><label>Berlaku sejak</label><input id="ak_tgl" type="date" value="${esc(a?.tglAwal ?? todayISO())}"></div>
      </div>
      <div class="row2">
        <div class="field"><label>Kode akun (COA) — boleh dikosongkan</label><input id="ak_kode" value="${esc(a?.kodeAkun ?? '')}" placeholder="1-1100"></div>
        <div class="field"><label>Catatan</label><input id="ak_cat" value="${esc(a?.catatan ?? '')}"></div>
      </div>
    </div>
    <div class="modal-foot"><button class="btn" id="ak_batal">Batal</button><button class="btn primary" id="ak_simpan">💾 Simpan</button></div>`);

  const v = (id: string): string => (bg.querySelector('#' + id) as HTMLInputElement | HTMLSelectElement).value;
  const saldo = bg.querySelector('#ak_saldo') as HTMLInputElement;
  saldo.addEventListener('input', () => { saldo.value = rpFmt(saldo.value); });
  const bankBox = bg.querySelector('#ak_bankbox') as HTMLElement;
  const jenis = bg.querySelector('#ak_jenis') as HTMLSelectElement;
  const kkBox = bg.querySelector('#ak_kkbox') as HTMLElement;
  const kk = bg.querySelector('#ak_kk') as HTMLInputElement;
  kk.addEventListener('input', () => { kk.value = rpFmt(kk.value); });
  const syncBank = (): void => {
    const tunai = jenis.value === 'tunai';
    bankBox.style.display = tunai ? 'none' : '';
    kkBox.style.display = tunai ? '' : 'none';     // rekening & QRIS tak punya laci
  };
  jenis.addEventListener('change', syncBank); syncBank();

  bg.querySelector('#ak_batal')!.addEventListener('click', () => bg.remove());
  bg.querySelector('#ak_simpan')!.addEventListener('click', async () => {
    const nama = v('ak_nama').trim();
    if (!nama) { toast('Nama akun wajib diisi'); return; }
    try {
      await repo.simpanAkun(a?.id ?? null, {
        nama, jenis: v('ak_jenis') as repo.AkunRow['jenis'], outlet: v('ak_outlet') || null,
        bankNama: v('ak_bank').trim() || null, noRek: v('ak_rek').trim() || null, atasNama: v('ak_an').trim() || null,
        saldoAwal: rpVal(v('ak_saldo')), tglAwal: v('ak_tgl') || null, aktif: a?.aktif ?? true,
        urut: +v('ak_urut') || 10, kodeAkun: v('ak_kode').trim() || null, catatan: v('ak_cat').trim() || null,
        kasKecil: v('ak_jenis') === 'tunai' ? rpVal(v('ak_kk')) : 0,
      });
      bg.remove(); toast('✅ Akun kas disimpan'); void akunKasTab(body, true);
    } catch (e) { toast((e as Error).message); }
  });
}

/** Dipakai form Aset: pilihan kelompok pajak → mengisi masa manfaat otomatis. */
export const opsiKelompok = (dipilih?: string | null): string =>
  Object.entries(KELOMPOK_ASET).map(([k, v]) =>
    `<option value="${k}" ${dipilih === k ? 'selected' : ''}>${esc(v.label)}</option>`).join('');
